import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, useMutation } from '../lib/convexApi';
import { useAuth } from '../contexts/AuthContext';

const CreateTournament: React.FC = () => {
  const navigate = useNavigate();
  const createTournament = useMutation(api.tournaments.createTournament);
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    club: '',
    format: '' as '' | 'americano' | 'king',
    maxPlayers: '' as '' | string,
    lockHours: 2,
    registrationType: '' as '' | 'individual' | 'couples',
    description: '',
  });
  
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setError('');

    try {
      if (!user?._id) {
        setError('Debes iniciar sesión para crear un torneo');
        return;
      }

      const dateTime = new Date(`${formData.date}T${formData.time}`);
      const lockTime = dateTime.getTime() - (formData.lockHours * 60 * 60 * 1000);

      const result = await createTournament({
        name: formData.name,
        date: dateTime.getTime(),
        time: formData.time,
        club: formData.club,
        format: formData.format as 'americano' | 'king',
        maxPlayers: parseInt(formData.maxPlayers),
        organizerId: user._id,
        lockTime,
        registrationType: formData.registrationType as 'individual' | 'couples',
        description: formData.description,
      });

      // Navigate to dashboard
      navigate(`/dashboard/${result.tournamentId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear el torneo');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-6">
          <img
            src="/torneopadel.png"
            alt="TorneoPadel"
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
        </div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Crear Torneo</h1>
          <Link to="/dashboard/organizer" className="text-blue-600 hover:text-blue-800">← Volver al Dashboard</Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Torneo
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ej. Americano Sábado por la Mañana"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha
                </label>
                <input
                  type="date"
                  id="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-2">
                  Hora
                </label>
                <input
                  type="time"
                  id="time"
                  required
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label htmlFor="club" className="block text-sm font-medium text-gray-700 mb-2">
                Club/Ubicación
              </label>
              <input
                type="text"
                id="club"
                required
                value={formData.club}
                onChange={(e) => setFormData(prev => ({ ...prev, club: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ej. Club de Padel Centro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-2">
                  Formato
                </label>
                <select
                  id="format"
                  required
                  value={formData.format}
                  onChange={(e) => setFormData(prev => ({ ...prev, format: e.target.value as '' | 'americano' | 'king' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona formato</option>
                  <option value="americano">Americano</option>
                  <option value="king">Rey de la Cancha</option>
                </select>
              </div>
              <div>
                <label htmlFor="registrationType" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Inscripción
                </label>
                <select
                  id="registrationType"
                  required
                  value={formData.registrationType}
                  onChange={(e) => setFormData(prev => ({ ...prev, registrationType: e.target.value as '' | 'individual' | 'couples' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona tipo</option>
                  <option value="individual">Individual</option>
                  <option value="couples">Por Parejas</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="maxPlayers" className="block text-sm font-medium text-gray-700 mb-2">
                Jugadores Máximos
              </label>
              <select
                id="maxPlayers"
                required
                value={formData.maxPlayers}
                onChange={(e) => setFormData(prev => ({ ...prev, maxPlayers: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Selecciona cantidad</option>
                <option value="4">4 jugadores</option>
                <option value="8">8 jugadores</option>
                <option value="12">12 jugadores</option>
                <option value="16">16 jugadores</option>
                <option value="20">20 jugadores</option>
                <option value="24">24 jugadores</option>
                <option value="32">32 jugadores</option>
              </select>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Instrucciones para los Jugadores
              </label>
              <textarea
                id="description"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ej. Costo: $200 por persona. Pagar en la cancha. Llegar 15 minutos antes. Incluye agua y pelotas. Premios para los ganadores."
              />
              <p className="mt-1 text-xs text-gray-500">
                Información importante como: costo, forma de pago, hora de llegada, premios, etc.
              </p>
            </div>

            <div>
              <label htmlFor="lockHours" className="block text-sm font-medium text-gray-700 mb-2">
                Cerrar Inscripciones (horas antes del torneo)
              </label>
              <select
                id="lockHours"
                value={formData.lockHours}
                onChange={(e) => setFormData(prev => ({ ...prev, lockHours: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={1}>1 hora antes</option>
                <option value={2}>2 horas antes</option>
                <option value={4}>4 horas antes</option>
                <option value={24}>1 día antes</option>
              </select>
            </div>

            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={isCreating}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-lg transition duration-200"
            >
              {isCreating ? 'Creando...' : 'Crear Torneo'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTournament;